/**
 * Slug Utilities für GmbH-Detailseiten
 * 
 * Erzeugt SEO-freundliche URLs im Format: /gmbh-kaufen/[slug]-[id]
 * - Slug dient der Lesbarkeit
 * - ID am Ende garantiert eindeutige Zuordnung
 * - Robust bei Umbenennungen und Duplikaten
 */

/**
 * Erzeugt einen URL-freundlichen Slug aus einem Titel
 * @param {string} title - Der Titel der Gesellschaft
 * @returns {string} URL-freundlicher Slug
 */
export const generateSlug = (title) => {
  if (!title) return '';
  
  return title
    .toLowerCase()
    // Deutsche Umlaute und ß ersetzen
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    // Sonderzeichen entfernen, Leerzeichen durch Bindestriche ersetzen
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    // Mehrfache Bindestriche zusammenfassen
    .replace(/-+/g, '-')
    // Führende und abschließende Bindestriche entfernen
    .replace(/^-|-$/g, '')
    // Maximale Länge für bessere URLs
    .slice(0, 60);
};

/**
 * Kombiniert Slug und ID zu einer vollständigen URL-Komponente
 * Format: [slug]-[id] (die letzten 8 Zeichen der UUID)
 * @param {string} title - Der Titel der Gesellschaft
 * @param {string} id - Die UUID der Gesellschaft
 * @returns {string} Kombinierter Slug mit ID-Suffix
 */
export const createListingSlug = (title, id) => {
  if (!id) return '';
  
  const slug = generateSlug(title);
  // Verwende die letzten 8 Zeichen der UUID für kürzere URLs
  const shortId = id.slice(-8);
  
  return slug ? `${slug}-${shortId}` : shortId;
};

/**
 * Extrahiert die ID aus einem kombinierten Slug
 * Der letzte Teil nach dem letzten Bindestrich ist die ID (8 Zeichen)
 * @param {string} slugWithId - Der kombinierte Slug mit ID
 * @returns {string|null} Die extrahierte Short-ID oder null
 */
export const extractIdFromSlug = (slugWithId) => {
  if (!slugWithId) return null;
  
  // Die letzten 8 Zeichen sind die Short-ID
  const parts = slugWithId.split('-');
  if (parts.length === 0) return null;
  
  const lastPart = parts[parts.length - 1];
  
  // Validierung: sollte 8 alphanumerische Zeichen sein
  if (/^[a-f0-9]{8}$/i.test(lastPart)) {
    return lastPart;
  }
  
  // Fallback: Vielleicht wurde die volle UUID übergeben
  if (/^[a-f0-9-]{36}$/i.test(slugWithId)) {
    return slugWithId;
  }
  
  return null;
};

/**
 * Erstellt den vollständigen Pfad zur Detailseite
 * @param {object} listing - Das Listing-Objekt mit title und id
 * @returns {string} Der vollständige Pfad
 */
export const getListingDetailPath = (listing) => {
  if (!listing?.id) return '/marktplatz';
  return `/gmbh-kaufen/${createListingSlug(listing.title, listing.id)}`;
};
